import { FC, useMemo, useState } from "react";
import { Box, Button, Tab, Tabs, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ScienceIcon from "@mui/icons-material/Science";
import WaterDropIcon from "@mui/icons-material/WaterDrop";
import { useSearchParams } from "react-router-dom";

import ConfigDeleteDialog, {
	type DeleteDependency,
} from "../../components/ConfigListShell/ConfigDeleteDialog";
import { ConfigListViewControls } from "../../components/ConfigListShell/ConfigListToolbar";
import { type ViewMode } from "../../components/ConfigListShell/useConfigList";
import { useFeedBack } from "../../providers/FeedBackProvider/FeedBackContext";
import { IAnalyte } from "../../domain/analyte.interface";
import { CupResponse } from "../../domain/cup.interface";
import { useAnalytes } from "../../hooks/useAnalytes";
import { useCupDependencies, useCups, useDeleteCup } from "../../hooks/useCups";
import {
	cupDeleteAllowedBody,
	cupDeleteBlockedSuggestion,
	cupDeleteDependencies,
	cupDeleteName,
	dependenciesFromConflict,
} from "./cupDelete";
import AnalytesPanel from "./AnalytesPanel";
import CupEditor from "./CupEditor";
import AnalyteEditor from "./AnalyteEditor";
import CupsPanel from "./CupsPanel";
import { vendorFilterOptions } from "./cupsList";
import { searchForTab, tabFromSearch, type MergedTab } from "./tabs";

/**
 * SIMS-8783 — Cups and analytes on one screen.
 *
 * Imports here are RELATIVE on purpose. The `@/*` alias in tsconfig is honoured by tsc and by
 * jest's moduleNameMapper, but NOT by CRA's webpack — react-scripts reads `baseUrl` and ignores
 * `paths`. Every `@/` import that works elsewhere in src/ resolves to a TYPE, which Babel erases
 * before webpack ever sees it (e.g. cup.service.ts:10 uses CupStatus only as an annotation).
 * Importing a runtime value through the alias compiles, type-checks and unit-tests green, then
 * fails to resolve the moment the dev server builds it.
 *
 * Three structural decisions, each recorded in construction/functional-design.md:
 *
 *  - The active tab lives in the URL, not in state. The Hi-Fi keeps it in state and loses it on
 *    reload; C17944 requires it to survive one.
 *  - `view` is held HERE and passed down, because the design shares one view across both tabs
 *    (markup 1002). Everything else — search, filter, sort, page — belongs to the panel, so
 *    switching tabs unmounts it and clears ALL of them. The design clears only the QUERY on a tab
 *    switch and deliberately leaves the filter set (second-reader D6, which records that asymmetry
 *    as a defect risk). Clearing everything is the stronger behaviour here, because the two tabs
 *    filter on different dimensions and a surviving vendor filter would be meaningless on
 *    analytes — but it IS a deviation, and it is the shape question Q7 asks about.
 *  - The header count states the WHOLE catalogue and never follows a filter. The per-tab result
 *    count is the one AC3 means (second-reader diff D2).
 */
const CupsAndAnalytes: FC = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const tab = tabFromSearch(`?${searchParams.toString()}`);

	const [view, setView] = useState<ViewMode>("table");

	// SIMS-8799 — the editor lives in component state on this screen, not on a route of its own.
	// That is what the design models (`s.page === 'cup'`, breadcrumb back to the list) and what
	// Foundation's worked example does (Payers/index.tsx:92,649).
	const [editing, setEditing] = useState<{ cup?: CupResponse } | null>(null);

	// SIMS-8814 — the analyte editor, held the same way the cup editor is: component state
	// on this screen, not a route of its own.
	const [editingAnalyte, setEditingAnalyte] = useState<{
		analyte?: IAnalyte;
	} | null>(null);

	// SIMS-8850 — the confirmation is the container's, not the panel's, so the cup editor can
	// reach the same dialog when SIMS-8799 wires its header Delete.
	const [deleting, setDeleting] = useState<CupResponse | null>(null);
	// Counts the SERVER refused with, kept when a delete loses the race against a visit being
	// recorded. They outrank the fetched ones: they are the reason the delete was refused.
	const [conflictCounts, setConflictCounts] = useState<
		DeleteDependency[] | null
	>(null);
	const deleteCup = useDeleteCup();
	const { showSnackBar } = useFeedBack();

	const {
		data: cupsData,
		isLoading: cupsLoading,
		isError: cupsError,
	} = useCups();
	const {
		data: analytesData,
		isLoading: analytesLoading,
		isError: analytesError,
	} = useAnalytes();

	const cups = useMemo<CupResponse[]>(() => cupsData?.body ?? [], [cupsData]);
	const analytes = useMemo<IAnalyte[]>(
		() => analytesData?.body ?? [],
		[analytesData],
	);
	const vendors = useMemo(() => vendorFilterOptions(cups), [cups]);

	const cupsSettled = !cupsLoading && !cupsError;
	const analytesSettled = !analytesLoading && !analytesError;

	const selectTab = (next: MergedTab) => {
		const search = searchForTab(next);
		setSearchParams(search ? new URLSearchParams(search) : {}, {
			replace: false,
		});
	};

	// Fetched only while the confirmation is open: the counts are needed for ONE cup at the moment
	// deletion is asked for, not for every row on every list load.
	const {
		data: dependencyCounts,
		isFetching: isCheckingDependencies,
		isError: dependenciesUnavailable,
	} = useCupDependencies(deleting?._id ?? null, Boolean(deleting));

	const deleteDependencies = useMemo(
		() => conflictCounts ?? cupDeleteDependencies(dependencyCounts),
		[conflictCounts, dependencyCounts],
	);

	const askDelete = (cup: CupResponse): void => {
		setConflictCounts(null);
		setDeleting(cup);
	};

	const closeDelete = (): void => {
		setDeleting(null);
		setConflictCounts(null);
	};

	const confirmDelete = async (): Promise<void> => {
		if (!deleting) {
			return;
		}
		try {
			await deleteCup.mutateAsync(deleting._id);
			showSnackBar("Cup deleted successfully", "success");
			closeDelete();
		} catch (error) {
			// AC2/AC3/AC4 from the SERVER. The dialog's counts were read when it opened; the guard
			// re-counts at delete time, so a cup that became used in between is refused here and
			// the dialog turns blocked with the server's numbers rather than closing on a failure.
			const refused = dependenciesFromConflict(error);
			if (refused) {
				setConflictCounts(refused);
				return;
			}
			showSnackBar("The cup could not be deleted.", "error");
		}
	};

	if (editingAnalyte) {
		return (
			<AnalyteEditor
				analyte={editingAnalyte.analyte}
				cups={cups}
				// AC3's usage list is derived from the cups collection, so an unsettled cups
				// query means the blast radius of a rename is UNKNOWN, not zero.
				cupsUnavailable={!cupsSettled}
				onExit={() => setEditingAnalyte(null)}
			/>
		);
	}

	if (editing) {
		return (
			<CupEditor
				cup={editing.cup}
				analytes={analytes}
				// The editor must be able to tell "no analytes to pick" from "the catalogue has not
				// arrived". Without these an unsettled query renders an empty checklist, and the
				// user can save a cup with nothing on it believing there was nothing to choose.
				analytesLoading={analytesLoading}
				analytesError={analytesError}
				onExit={() => setEditing(null)}
			/>
		);
	}

	return (
		<Box sx={{ p: 3, display: "flex", flexDirection: "column", minHeight: 0 }}>
			<Box sx={{ display: "flex", alignItems: "flex-end", gap: 2 }}>
				<Box>
					<Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
						Program setup
					</Typography>
					<Typography
						component="h1"
						sx={{ fontSize: "1.5rem", lineHeight: "2rem", fontWeight: 600 }}
					>
						Cups &amp; analytes
					</Typography>
					{/* The whole catalogue. Deliberately NOT the match count — see D2. A count is
					    withheld until its query settles: stating "0 cups" while the skeleton is on
					    screen, or after a failed load, is an absence of data dressed as a fact. */}
					<Typography
						sx={{ fontSize: "0.8125rem", color: "text.secondary" }}
						data-testid="cups-analytes-total"
					>
						{`${cupsSettled ? cups.length : "—"} cups · ${
							analytesSettled ? analytes.length : "—"
						} analytes · ${cupsSettled ? vendors.length : "—"} ${
							vendors.length === 1 && cupsSettled ? "vendor" : "vendors"
						}`}
					</Typography>
				</Box>
				<Box sx={{ flex: 1 }} />
				<Button
					variant="contained"
					startIcon={<AddIcon />}
					onClick={() =>
						tab === "cups" ? setEditing({}) : setEditingAnalyte({})
					}
				>
					{tab === "cups" ? "Create cup" : "Create analyte"}
				</Button>
				{/* Rows-per-page is NOT here: the markup puts it in each list's footer (D8). */}
				<ConfigListViewControls
					view={view}
					onViewChange={setView}
					pageSize="10"
					onPageSizeChange={() => undefined}
					pageSizeOptions={[]}
					showPageSize={false}
				/>
			</Box>

			<Tabs
				value={tab}
				onChange={(_, next: MergedTab) => selectTab(next)}
				sx={{ borderBottom: 1, borderColor: "divider" }}
			>
				<Tab
					value="cups"
					icon={<WaterDropIcon fontSize="small" />}
					iconPosition="start"
					label={`Cups ${cupsSettled ? cups.length : "—"}`}
				/>
				<Tab
					value="analytes"
					icon={<ScienceIcon fontSize="small" />}
					iconPosition="start"
					label={`Analytes ${analytesSettled ? analytes.length : "—"}`}
				/>
			</Tabs>

			{tab === "cups" ? (
				<CupsPanel
					cups={cups}
					analytes={analytes}
					onEdit={(cup) => setEditing({ cup })}
					onDelete={askDelete}
					isLoading={cupsLoading}
					isError={cupsError}
					view={view}
				/>
			) : (
				<AnalytesPanel
					analytes={analytes}
					cups={cups}
					cupsUnavailable={!cupsSettled}
					onEdit={(analyte) => setEditingAnalyte({ analyte })}
					isLoading={analytesLoading}
					isError={analytesError}
					view={view}
				/>
			)}

			{/* SIMS-8850. The shared dialog already carries the four states this story needs —
			    checking, unavailable, blocked, allowed — and withholds Delete for all but the
			    last. The blocked state has no Delete button at all, which is UI, not the guard:
			    DELETE /cups/:id re-counts server-side and refuses on its own (PR #1353). */}
			{deleting && (
				<ConfigDeleteDialog
					open
					recordName={cupDeleteName(deleting)}
					recordNoun="cup"
					dependencies={deleteDependencies}
					// Once the server has refused, its answer stands. Without the guard a window
					// refocus refetches (staleTime: 0) and flips the refusal back to "Checking…".
					isCheckingDependencies={isCheckingDependencies && !conflictCounts}
					dependenciesUnavailable={dependenciesUnavailable}
					dependencyNoun="treatment plans and recorded visits"
					blockedSuggestion={cupDeleteBlockedSuggestion}
					allowedBody={cupDeleteAllowedBody(deleting)}
					isDeleting={deleteCup.isPending}
					onConfirm={confirmDelete}
					onClose={closeDelete}
				/>
			)}
		</Box>
	);
};

export default CupsAndAnalytes;
